import Image from "next/image";
import type { ReactNode } from "react";

export type BrowserMockupCardProps = {
  label: string;
  labelClassName?: string;
  imageSrc: string;
  imageAlt: string;
  imageClassName?: string;
  imageScale?: number;
  cardClassName?: string;
  badge?: string;
  browserUrl?: string;
  afterMockup?: ReactNode;
  bullets: string[];
  bulletClassName?: string;
  mockupOnly?: boolean;
  hideLabel?: boolean;
  wrapperClassName?: string;
  viewportClassName?: string;
  imageSizes?: string;
};

export function BrowserMockupCard({
  label,
  labelClassName = "text-white/50",
  imageSrc,
  imageAlt,
  imageClassName = "",
  imageScale = 1,
  cardClassName = "",
  badge,
  browserUrl = "www.star-limousine-paris.com",
  afterMockup,
  bullets,
  bulletClassName = "text-white/80",
  mockupOnly = false,
  hideLabel = false,
  wrapperClassName = "",
  viewportClassName = "h-[240px] w-full sm:h-[300px] lg:h-[340px]",
  imageSizes = "(max-width: 768px) 100vw, 50vw",
}: BrowserMockupCardProps) {
  return (
    <div
      className={
        mockupOnly
          ? "contents"
          : "flex w-full min-w-0 flex-col gap-4"
      }
    >
      {!hideLabel ? (
        <p className={`text-center text-xs font-extrabold uppercase tracking-[0.2em] sm:text-sm ${labelClassName}`}>
          {label}
        </p>
      ) : null}

      <div
        className={`w-full overflow-hidden rounded-2xl border bg-[#1C1C1C] shadow-lg ${wrapperClassName} ${
          cardClassName || "border-white/10"
        }`}
      >
        <div className="flex items-center gap-2 border-b border-white/10 bg-[#141414] px-3 py-2.5 sm:px-4">
          <div className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          </div>
          <div className="min-w-0 flex-1 rounded-md border border-white/10 bg-[#0f0f0f] px-3 py-1">
            <p className="truncate text-center text-[10px] text-white/40 sm:text-xs">
              {browserUrl}
            </p>
          </div>
        </div>

        <div className={`relative overflow-hidden bg-[#0a0a0a] ${viewportClassName}`}>
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            className={`object-cover object-top ${imageClassName}`}
            style={imageScale !== 1 ? { transform: `scale(${imageScale})`, transformOrigin: "top center" } : undefined}
            sizes={imageSizes}
            loading="lazy"
          />
          {badge ? (
            <div className="absolute right-3 top-3 rounded-full border border-[#71DDAE]/40 bg-[#1C1C1C]/90 px-3 py-1.5 text-xs font-extrabold text-[#71DDAE] shadow-[0_8px_24px_rgba(113,221,174,0.35)] backdrop-blur-sm sm:text-sm">
              {badge}
            </div>
          ) : null}
        </div>
      </div>

      {!mockupOnly && afterMockup}

      {!mockupOnly && bullets.length > 0 ? (
        <ul className={`space-y-2 text-sm leading-relaxed sm:text-base ${bulletClassName}`}>
          {bullets.map((bullet) => (
            <li key={bullet} className="flex items-start gap-2">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-70" aria-hidden="true" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
